import { useEffect, useLayoutEffect, useRef, useSyncExternalStore } from 'react';
import type { ManagedOrganizationMember } from '@wx-weiqi/contracts';
import type { AdminStore } from '../stores/admin-store';
import type { QuestionBankStore } from '../stores/question-bank-store';
import type { TeachingStore } from '../stores/teaching-store';
import { QuestionBankPanel } from './QuestionBankPanel';
import { RoleDialogHeading } from './RoleDialogHeading';
import { StudentInformationPanel } from './StudentInformationPanel';
import { TeacherAiManagementPanel } from './TeacherAiManagementPanel';
import './teacher-textbook-shell.css';

export type TeacherTextbookManagementView = 'questions' | 'students' | 'ai';

const TITLES = { questions: '题库管理', students: '学生信息', ai: 'AI管理' } as const;

/** Reuses the existing management panels without discarding the selected textbook. */
export function TeacherTextbookManagementDialog({
  view,
  admin,
  teaching,
  questionBank,
  onClose,
  onOpenReview,
}: {
  readonly view: TeacherTextbookManagementView;
  readonly admin: AdminStore;
  readonly teaching: TeachingStore;
  readonly questionBank: QuestionBankStore;
  readonly onClose: () => void;
  readonly onOpenReview: (gameId: string, student: ManagedOrganizationMember) => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const teachingState = useSyncExternalStore(teaching.subscribe, teaching.snapshot);
  const questionBankState = useSyncExternalStore(questionBank.subscribe, questionBank.snapshot);
  const adminState = useSyncExternalStore(admin.subscribe, admin.snapshot);
  const busy =
    view === 'students'
      ? teachingState.pending !== null
      : view === 'questions'
        ? questionBankState.pending !== null
        : adminState.pending !== null;

  useLayoutEffect(() => {
    const element = dialog.current;
    const previous = document.activeElement;
    element?.showModal();
    return () => {
      element?.close();
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus();
    };
  }, []);

  useEffect(() => {
    if (view === 'students') {
      teaching.clearError();
      void teaching.loadTeacherDashboard().catch(() => undefined);
    } else if (view === 'ai') {
      void admin.loadAiManagement().catch(() => undefined);
    } else {
      questionBank.clearMessages();
    }
  }, [view, teaching, admin, questionBank]);

  return (
    <dialog
      ref={dialog}
      className="teacher-textbook-management-dialog role-dialog role-dialog--teacher"
      aria-label={TITLES[view]}
      onCancel={(event) => {
        event.preventDefault();
        // Existing panels own their edit dialogs; Escape must not discard them with the parent.
        const editing = event.currentTarget.querySelector('[role="dialog"][aria-modal="true"]');
        if (!busy && !editing) onClose();
      }}
    >
      <section
        className={`lobby-dialog-card lobby-dialog-card--wide${view === 'questions' ? ' lobby-dialog-card--question-bank' : view === 'students' ? ' lobby-dialog-card--student-info' : ''}`}
      >
        <RoleDialogHeading role="TEACHER" title={TITLES[view]} busy={busy} onClose={onClose} />
        {view === 'questions' ? <QuestionBankPanel store={questionBank} /> : null}
        {view === 'students' ? (
          <StudentInformationPanel
            teaching={teaching}
            state={teachingState}
            onOpenReview={onOpenReview}
          />
        ) : null}
        {view === 'ai' ? <TeacherAiManagementPanel admin={admin} /> : null}
      </section>
    </dialog>
  );
}
